// WallBG.jsx - Shared component for displaying background in wall view
import anime from 'animejs';
import React, { useEffect, useRef, useState } from 'react';

export const WallBG = ({ wallData = {} }) => {
  const wallAspectRatio = wallData?.wallAspectRatio || 'w-dvw h-dvh';
  const wallMaxH = wallData?.wallMaxH || '';
  const isVoting = wallData?.wallMessage?.postItem?.postTypeForWall === 'vote';
    
  const BGShaderShow = 
    wallData?.wallSettings?.wallBg?.BGShaderShow ?? false;
  const BGFadeDuration =
    wallData?.wallSettings?.wallBg?.BGFadeDuration || 1000;
  const BGEffectTranslateYIn =
    wallData?.wallSettings?.wallBg?.BGEffectTranslateYIn || '0';
  const BGEffectTranslateYOut =
    wallData?.wallSettings?.wallBg?.BGEffectTranslateYOut || '0';
  const BGEffectTranslateXIn =
    wallData?.wallSettings?.wallBg?.BGEffectTranslateXIn || '0';
  const BGEffectTranslateXOut =
    wallData?.wallSettings?.wallBg?.BGEffectTranslateXOut || '0';
  const BGEffectScaleIn =
    wallData?.wallSettings?.wallBg?.BGEffectScaleIn || '1.1';
  const BGEffectScaleOut =
    wallData?.wallSettings?.wallBg?.BGEffectScaleOut || '1.1';
  const BGEffectBlur = wallData?.wallSettings?.wallBg?.BGEffectBlur || '25px';
  const BGEffectEaseIn =
    wallData?.wallSettings?.wallBg?.BGEffectEaseIn || 'easeOutSine';
  const BGEffectEaseOut =
    wallData?.wallSettings?.wallBg?.BGEffectEaseOut || 'easeOutCubic';

  const refBG1 = useRef(null);
  const refBG2 = useRef(null);

  const [isBG1Visible, setIsBG1Visible] = useState(false);
  const [bg1, setBg1] = useState(null);
  const [bg2, setBg2] = useState(null);
  const animeScope = useRef(null);

  // console.log('WallBG render', { wallData, bg1, bg2, isBG1Visible });

  // Handle crossfade when both backgrounds are set
  useEffect(() => {
    // Only run crossfade when both backgrounds exist
    if (!bg1 || !bg2) return;

    const inBG = isBG1Visible ? refBG1.current : refBG2.current;
    const outBG = isBG1Visible ? refBG2.current : refBG1.current;

    if (!inBG) return;

    if (animeScope.current) {
      animeScope.current.pause();
    }

    animeScope.current = anime({
      targets: inBG,
      opacity: 1,
      translateY: [BGEffectTranslateYIn, '0'],
      translateX: [BGEffectTranslateXIn, '0'],
      scale: [BGEffectScaleIn, 1],
      filter: ['blur(' + BGEffectBlur + ')', 'blur(0px)'],
      easing: BGEffectEaseIn,
      duration: BGFadeDuration,
      begin: () => {
        if (outBG) {
          anime({
            targets: outBG,
            opacity: 0,
            translateY: ['0', BGEffectTranslateYOut],
            translateX: ['0', BGEffectTranslateXOut],
            scale: [1, BGEffectScaleOut],
            filter: ['blur(0px)', 'blur(' + BGEffectBlur + ')'],
            easing: BGEffectEaseOut,
            duration: BGFadeDuration,
          });
        }
      },
    });
  }, [isBG1Visible, bg1, bg2]);

  useEffect(() => {
    const newUrl = wallData?.wallBg?.currentMedia?.url;
    if (!newUrl) {
      if (bg1 || bg2) {
        setBg1(null);
        setBg2(null);
      }
      return;
    }
    if (newUrl !== (isBG1Visible ? bg1?.url : bg2?.url)) {
      if (isBG1Visible) setBg2(wallData?.wallBg?.currentMedia);
      else setBg1(wallData?.wallBg?.currentMedia);

      setIsBG1Visible((isBG1Visible) => !isBG1Visible);
    }
  }, [wallData?.wallBg?.currentMedia]);

  const wallBGColor = 'bg-black';
  const wallBGStyle = ['z-10 w-full h-auto overflow-hidden', wallBGColor].join(' ');
  const wallBGShader = `before:absolute before:size-full before:z-10 before:bg-black/25 before:transition-all ${BGShaderShow ? "before:opacity-100" : "before:opacity-0"}`;
  const wallBGSubStyle = 'absolute size-full';
  const wallBGMediaStyle =
    'block object-cover object-center w-full h-full border-none outline-none will-change-transform backface-hidden';

  const mediaComponent = (media) => {
    if (!media?.url) return null;
    if (media?.type === 'video') {
      return (
        <video
          className={wallBGMediaStyle}
          src={media?.url}
          autoPlay
          loop
          muted
          playsInline
          controls={false}
          preload="metadata"
          webkit-playsinline="true"
          onLoadedData={(e) => {
            e.target.play().catch(() => {});
          }}
        />
      );
    } else {
      return (
        <img
          className={wallBGMediaStyle}
          src={media?.url}
          alt=""
          loading="eager"
          decoding="async"
          draggable={false}
          onError={() => {}}
        />
      );
    }
  };

  return (
    <div
      id="wallBG"
      className={[wallBGStyle, wallAspectRatio, wallMaxH, wallBGShader].join(
        ' '
      )}
    >
      {bg1 && (
        <div
          id="wallBG1"
          ref={refBG1}
          className={[wallBGSubStyle, bg2 ? 'opacity-0' : '', isVoting ? 'wallVoting' : ''].join(' ')}
        >
          {mediaComponent(bg1)}
        </div>
      )}

      {bg2 && (
        <div
          id="wallBG2"
          ref={refBG2}
          className={[wallBGSubStyle, bg1 ? 'opacity-0' : '', isVoting ? 'wallVoting' : ''].join(' ')}
        >
          {mediaComponent(bg2)}
        </div>
      )}
    </div>
  );
};

export const areWallBGPropsEqual = (prevProps, nextProps) => {
  const relevantPrevProps = {
    wallAspectRatio: prevProps.wallData?.wallAspectRatio,
    // #248: compare the whole wallSettings object so any client-settings change
    // (visibility toggles, animation params) re-renders the memoized wall.
    wallSettings: prevProps.wallData?.wallSettings,
    wallBg: prevProps.wallData?.wallBg,
    postTypeForWall: prevProps.wallData?.wallMessage?.postItem?.postTypeForWall,
  };

  const relevantNextProps = {
    wallAspectRatio: nextProps.wallData?.wallAspectRatio,
    wallSettings: nextProps.wallData?.wallSettings,
    wallBg: nextProps.wallData?.wallBg,
    postTypeForWall: nextProps.wallData?.wallMessage?.postItem?.postTypeForWall,
  };

  return (
    JSON.stringify(relevantPrevProps) === JSON.stringify(relevantNextProps)
  );
};

export const WallBGMemo = React.memo(WallBG, areWallBGPropsEqual);
